import {
	Box,
	Button,
	Chip,
	CircularProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select,
	SelectChangeEvent,
	Tooltip,
	Typography,
} from "@mui/material";
import {CommentOutlined, Edit, Inbox, LockPerson, OpenInNew} from "@mui/icons-material";
import React, {useEffect, useMemo, useState} from "react";
import {BreadcrumbsComponent} from "../../../../components/common/breadcrumbs-component/BreadcrumbsComponent";
import {GraphQlDataGrid} from "../../../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {useNavigate} from "react-router";
import {GET_DOCUMENT_LIST} from "../graphql/queries/gqlGetDocumentList";
import {GridRenderCellParams} from "@mui/x-data-grid";
import moment from "moment/moment";
import {GET_SOURCECOUNTS} from "../graphql/queries/gqlGetSourceCounts";
import {GET_DOCUMENTS_BY_SOURCE} from "../graphql/queries/gqlGetDocumentListBySource";
import {translateFunction} from "../../../../localization/TranslationService";
import {DocumentStatus} from "../../models/DocumentStatus";
import {pageAuthorizedByGroup} from "../../../fragments/PageAuthorization";
import {GridRowSelectionModel} from "@mui/x-data-grid/models/gridRowSelectionModel";
import {GenericModal} from "../../../querytool/components/generic-modal/GenericModal";
import {MultiEditCard} from "../multiEdit/multiEditCard";

const translate = translateFunction("SureBaseDocuments");

const containerStyling: React.CSSProperties = {
	display: "flex",
	flexDirection: "row",
};

const filterContainerStyling: React.CSSProperties = {
	flexGrow: 2,
	marginRight: "1em",
};

const tableContainerStyling: React.CSSProperties = {
	flexGrow: 12,
};

const selectedFilterStyling: React.CSSProperties = {
	borderRadius: "6px",
	backgroundColor: "#e0e0e0",
};

function DocumentsFx (){
	const navigate = useNavigate();
	const [loading, setLoading] = useState<boolean>(false);
	const [multiEdit, setMultiEdit] = useState<boolean>(false);
	const [selectedSource, setSelectedSource] = useState(0);
	const [selectedStatus, setSelectedStatus] = useState<DocumentStatus>();
	const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);

	const [sources, setSources] = useState<
		{ id: number; source: string; count: number }[]
	>([]);

	useEffect(() => {
		setSelectedSource(parseInt(localStorage.getItem("documents.selectedSource")??"0"));
		let status = localStorage.getItem("documents.selectedStatus");
		status= status === "undefined" ? null : status;
		setSelectedStatus(JSON.parse(status??JSON.stringify(DocumentStatus.Manual)));
	}, []);

	useEffect(() => {
		if (selectedSource !== undefined) {
			window.localStorage.setItem("documents.selectedSource", selectedSource.toString());
		}
		if (selectedStatus !== undefined) {
			window.localStorage.setItem("documents.selectedStatus", JSON.stringify(selectedStatus));
		}

	}, [selectedSource, selectedStatus]);

	const changeSelectedSource = (source: number) => {
		if (source === selectedSource) {
			setSelectedSource(0);
		} else {
			setSelectedSource(source);
		}
	};

	const changeSelectedStatus = (event: SelectChangeEvent) => {
		setSelectedStatus(DocumentStatus[getStatusKey(event.target.value) as keyof typeof DocumentStatus]);
	};
	const fetchSources = () => {
		setLoading(true);
		graphQLServices.documentsClient
			.query({ query: GET_SOURCECOUNTS, variables: {status: selectedStatus} })
			.then((result) => setSources(result.data.sourceCounts))
			.finally(() => setLoading(false));

	};

	function getStatusKey(value: string): string | undefined {
		return Object.keys(DocumentStatus).find(key => DocumentStatus[key as keyof typeof DocumentStatus] === value);
	}
	const navigateToDocument = (documentId: number) => {
		navigate("/documents/" + documentId);
	};

	useEffect(() => {
		if (selectedStatus) {
			fetchSources();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedStatus]);

	const queryVars = useMemo(() => {
		return {source: selectedSource, status: selectedStatus };
	}, [selectedSource, selectedStatus]);

	if (loading) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}

	const multiEditButton = (): React.ReactNode => {
		if (rowSelectionModel && rowSelectionModel.length > 0)
			return (
				<Button onClick={() => setMultiEdit(true)} title={"Multi Edit"}><Edit/>{translate("Edit Selection")}</Button>
			);
		return <></>;
	};


	return (
		<>
			<BreadcrumbsComponent />
			{   multiEdit && rowSelectionModel && <GenericModal open={true} title={translate("Multi Edit")} onClose={() => {
				setMultiEdit(false); fetchSources();
			}}>
				<MultiEditCard documentIds={rowSelectionModel.map(x=> x as number)} />
			</GenericModal>}

			<div style={containerStyling}>
				<div style={filterContainerStyling}>
					<Typography variant="h6">{translate("Sources")}</Typography>
					<Select
						labelId="step-select-label"
						id="step-select"
						value={selectedStatus}
						label={translate("Step")}
						onChange={changeSelectedStatus}
					>
						<MenuItem value={DocumentStatus.Queued as string}>{translate(getStatusKey(DocumentStatus.Queued))}</MenuItem>
						<MenuItem value={DocumentStatus.Imported}>{translate(getStatusKey(DocumentStatus.Imported))}</MenuItem>
						<MenuItem value={DocumentStatus.Extracted}>{translate(getStatusKey(DocumentStatus.Extracted))}</MenuItem>
						<MenuItem value={DocumentStatus.Manual}>{translate(getStatusKey(DocumentStatus.Manual))}</MenuItem>
						<MenuItem value={DocumentStatus.Spam}>{translate(getStatusKey(DocumentStatus.Spam))}</MenuItem>
						<MenuItem value={DocumentStatus.Processed}>{translate(getStatusKey(DocumentStatus.Processed))}</MenuItem>
						<MenuItem value={DocumentStatus.Validated}>{translate(getStatusKey(DocumentStatus.Validated))}</MenuItem>
					</Select>

					<List>
						{sources.map((it) => {
							return (
								<ListItem
									style={
										selectedSource === it.id ? selectedFilterStyling : {}
									}
									disablePadding
									key={it.id}
								>
									<ListItemButton
										onClick={() => changeSelectedSource(it.id)}
									>
										<ListItemIcon>
											<Inbox />
										</ListItemIcon>
										<ListItemText primary={`${it.source} (${it.count})`} />
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>
				</div>
				{selectedSource > 0 &&
				<div style={tableContainerStyling}>
					<GraphQlDataGrid
						id={"documents"}
						graphFunction={"documentList"}
						rows={[]}
						hideExportButton
						initialPageSize={25}
						rowsPerPageOptions={[5, 10, 20]}
						//autoHeight={true}
						customButtons={[multiEditButton()]}
						checkboxSelection={selectedStatus === DocumentStatus.Manual}
						onRowSelectionModelChange={(newRowSelectionModel) => {
							setRowSelectionModel([...newRowSelectionModel]);
						}}
						rowSelectionModel={rowSelectionModel}

						onRowClick={(params) => navigateToDocument(params.row.id)}

						infiniteLoading
						gridHeight={800}
						enableHeaderFilter
						client={graphQLServices.documentsClient}
						query={
							selectedSource ? GET_DOCUMENTS_BY_SOURCE : GET_DOCUMENT_LIST
						}
						queryVariables={queryVars}
						defaultSortModel={[
							{
								field: "created",
								sort: "asc"
							}
						]}
						initialState={{
							columns: {
								columnVisibilityModel: {
									id: true
								},
							},
						}}
						columns={[
							{
								field: "id",
								headerName: "Id",
								width: 100,
								sortable: true,
								renderCell: (params: GridRenderCellParams) => {
									return (
										<>
											{params.row.id}
											<Tooltip title={translate("Open in new Tab")}>
												<a target="_blank"  rel="noreferrer"
													onClick={(e) => e.stopPropagation()}
													href={"/documents/" + params.row.id + "?fullpage"}>
													<OpenInNew/>
												</a>
											</Tooltip>
										</>
									);
								}
							},
							{
								field: "lock.userName",
								headerName: translate("Locked"),
								width: 80,
								filterable: false,
								sortable: false,
								renderHeader: (() => <LockPerson/>),
								renderCell: (params: GridRenderCellParams) => {
									return (
										<>
											{params.row.lock?.userName &&
												<Tooltip aria-multiline={true} title={<>
													<div style={{ whiteSpace: "pre-line" }}>{params.row.lock.userName}</div>
													<div style={{ whiteSpace: "pre-line" }}>{moment(params.row.lock.timeStamp).format("DD-MM-YYYY HH:mm:ss")}</div></>
												}>
													<Chip variant={"outlined"} label=<LockPerson/>/>
												</Tooltip>
											}
										</>
									);
								},
							},
							{
								field: "comment",
								headerName: translate("Comment"),
								width: 100,
								sortable: false,
								renderHeader: (() => <CommentOutlined/>),
								renderCell: (params: GridRenderCellParams) => {
									return (
										<>
											{params.row.comment &&
												<Tooltip aria-multiline={true} title={
													<div style={{ whiteSpace: "pre-line" }}>{params.row.comment}</div>
												}>
													<Chip variant={"outlined"} label={params.row.comment}/>
												</Tooltip>
											}
										</>
									);
								},
							},
							{
								field: "newTask",
								headerName: translate("Task"),
								width: 100,
								sortable: true,
								type: "boolean"
							},
							{
								field: "from",
								headerName: translate("From"),
								width: 200,
								sortable: true,
							},
							{
								field: "to",
								headerName: translate("To"),
								width: 200,
								sortable: true,
							},
							{
								field: "customer",
								headerName: translate("Customer"),
								width: 120,
								sortable: true,
								operator: "startswith"
							},
							{
								field: "customerId",
								headerName: translate("Customer Nr"),
								width: 120,
								sortable: false,
								operator: "equals"
							},
							{
								field: "description",
								headerName: translate("Description"),
								width: 300,
								sortable: true,
							},
							{
								field: "category",
								headerName: translate("Category"),
								width: 150,
								sortable: true,
								operator: "startswith"
							},
							{
								field: "created",
								headerName: translate("Received"),
								width: 200,
								sortable: true,
								renderCell: (params: GridRenderCellParams) => {
									return (
										<>
											{params.row.created &&
												moment(params.row.created).format("DD-MM-YYYY HH:mm:ss")}
										</>
									);
								},
							},

						]}
						persistSorting
						persistFilters
					/>
				</div>
				}
			</div>
		</>
	);
}

export const Documents = pageAuthorizedByGroup(DocumentsFx, ["Admin", "Documents"]);
